import frequency from "../enums/frequency";

const isDuplicateDate = (generatedDate, originalDate) => {
    const formattedGenerateDate = generatedDate?.split("T")?.[0];
    const formattedOriginalData = originalDate?.split("T")?.[0];

    return formattedOriginalData === formattedGenerateDate;
};

const tryAddNonRecurringEvent = (date, endTime, allDay, startDate, endDate, event, eventList) => {
    const eventDate = new Date(date);

    if (allDay) {
        eventDate.setHours(23, 59, 59, 999);
    } else {
        const [endHour, endMinute] = endTime?.split(":") ?? [0, 0];
        eventDate.setHours(endHour, endMinute, 0, 0);
    }

    if (eventDate && eventDate >= startDate && eventDate <= endDate) {
        eventList.push({
            ...event
        });

        return true;
    }
    return false;
};

const tryAddRecurringEvent = (
    recurringDate,
    startDate,
    endDate,
    eventDate,
    eventList,
    complementaryObject = {}
) => {
    const recurringValidation =
        recurringDate &&
        recurringDate >= startDate &&
        recurringDate <= endDate &&
        !isDuplicateDate(recurringDate.toISOString(), eventDate);

    if (recurringValidation) {
        eventList.push({
            ...complementaryObject,
            date: recurringDate.toISOString()
        });
        return true;
    }
    return false;
};

const getLastWeekdayOfMonth = (year, month, dayOfWeek) => {
    const lastDayOfMonth = new Date(year, month + 1, 0, 0, 0, 0, 0);

    let day = lastDayOfMonth.getDate();

    while (lastDayOfMonth.getDay() !== dayOfWeek) {
        lastDayOfMonth.setDate(--day);
    }

    return lastDayOfMonth;
};

const getNthWeekdayOfMonth = (year, month, dayOfWeek, nthWeek) => {
    const firstDayOfMonth = new Date(year, month, 1, 0, 0, 0, 0);

    let day = 1;

    while (firstDayOfMonth.getDay() !== dayOfWeek) {
        firstDayOfMonth.setDate(++day);
    }

    const targetDate = new Date(firstDayOfMonth);

    targetDate.setDate(firstDayOfMonth.getDate() + (nthWeek - 1) * 7);

    return targetDate.getMonth() === month ? targetDate : null;
};

const formatDates = (startDate, endDate, lastYear, filter) => {
    const formattedStartDate = new Date(startDate);
    const formattedEndDate = endDate ? new Date(endDate) : new Date(lastYear, 11, 31, 0, 0, 0, 0);

    const formattedMaxDate = filter?.max ? new Date(filter.max) : null;

    for (let date of [formattedStartDate, formattedEndDate, formattedMaxDate]) {
        date?.setHours?.(0, 0, 0, 0);
    }

    let localEndDate =
        !!formattedMaxDate && formattedMaxDate < formattedEndDate
            ? formattedMaxDate
            : formattedEndDate;

    return { startDate: formattedStartDate, endDate: localEndDate };
};

const generateOnTheRecurrence = (
    year,
    month,
    startDate,
    endDate,
    eventDate,
    daysOfMonth,
    complementaryObject
) => {
    const localRecurringEvents = [];

    daysOfMonth.forEach((day) => {
        const daysInMonth = new Date(year, month, 0, 0, 0, 0, 0).getDate();
        if (day > daysInMonth) return;

        const recurringDate = new Date(year, month, day, 0, 0, 0, 0);

        tryAddRecurringEvent(
            recurringDate,
            startDate,
            endDate,
            eventDate,
            localRecurringEvents,
            complementaryObject
        );
    });

    return localRecurringEvents;
};

const generateOnRecurrence = (
    year,
    month,
    startDate,
    endDate,
    eventDate,
    weeksOfMonth,
    daysOfWeek,
    complementaryObject
) => {
    const localRecurringEvents = [];

    weeksOfMonth.forEach((week) => {
        daysOfWeek.forEach((dayOfWeek) => {
            const adjustedDayOfWeek = dayOfWeek - 1;
            let recurringDate;

            if (week === -1) {
                recurringDate = getLastWeekdayOfMonth(year, month, adjustedDayOfWeek);
            } else {
                recurringDate = getNthWeekdayOfMonth(year, month, adjustedDayOfWeek, week);
            }

            tryAddRecurringEvent(
                recurringDate,
                startDate,
                endDate,
                eventDate,
                localRecurringEvents,
                complementaryObject
            );
        });
    });

    return localRecurringEvents;
};

const generateWeeklyRecurrence = (startDate, endDate, event, complementaryObject) => {
    const localRecurringEvents = [];
    let currentDate = new Date(startDate);
    currentDate.setHours(0, 0, 0, 0);

    while (currentDate <= endDate) {
        const startDay = currentDate.getDay();
        if (event.recurrence.daysOfWeek.length > 0) {
            event.recurrence.daysOfWeek.sort().forEach((dayOfWeek) => {
                const dayOfWeekForNewDateWeek = dayOfWeek - 1;
                const DAYS_OF_WEEK = 7;

                if (startDay > dayOfWeekForNewDateWeek) return;

                const dayOffset =
                    (dayOfWeekForNewDateWeek - currentDate.getDay() + DAYS_OF_WEEK) % DAYS_OF_WEEK;

                let recurringDate = new Date(currentDate);
                recurringDate.setHours(0, 0, 0, 0);
                recurringDate.setDate(recurringDate.getDate() + dayOffset);

                tryAddRecurringEvent(
                    recurringDate,
                    startDate,
                    endDate,
                    event.date,
                    localRecurringEvents,
                    complementaryObject
                );
            });
        }

        // Avança para o próximo ciclo semanal, levando em consideração a frequência (quantity)
        currentDate.setDate(currentDate.getDate() + 7 * event.recurrence.quantity - startDay);
    }

    return [...localRecurringEvents];
};

const generateDailyRecurrence = (startDate, endDate, event, complementaryObject) => {
    const localRecurringEvents = [];

    let recurringDate = new Date(startDate);
    recurringDate.setHours(0, 0, 0, 0);

    while (recurringDate <= endDate) {
        tryAddRecurringEvent(
            recurringDate,
            startDate,
            endDate,
            event.date,
            localRecurringEvents,
            complementaryObject
        );

        recurringDate.setDate(recurringDate.getDate() + event.recurrence.quantity);
    }

    return [...localRecurringEvents];
};

const generateMonthlyRecurrence = (startDate, endDate, event, complementaryObject) => {
    const localRecurringEvents = [];
    let currentDate = new Date(startDate);
    currentDate.setHours(0, 0, 0, 0);

    while (currentDate <= endDate) {
        const selectedYear = currentDate.getFullYear();
        const month = currentDate.getMonth(); // meses 1-indexados
        const complementaryObject = {
            ...event,
            firstEventDate: event.date
        };

        if (event.recurrence.isOnThe) {
            localRecurringEvents.push(
                ...generateOnTheRecurrence(
                    selectedYear,
                    month,
                    startDate,
                    endDate,
                    event.date,
                    event.recurrence.daysOfMonth,
                    complementaryObject
                )
            );
        } else if (event.recurrence.isOn) {
            localRecurringEvents.push(
                ...generateOnRecurrence(
                    selectedYear,
                    month,
                    startDate,
                    endDate,
                    event.date,
                    event.recurrence.weeksOfMonth,
                    event.recurrence.daysOfWeek,
                    complementaryObject
                )
            );
        }

        // Avança para o próximo ciclo mensal, de acordo com a quantidade definida
        currentDate.setMonth(currentDate.getMonth() + event.recurrence.quantity);
    }

    return [...localRecurringEvents];
};

const generateYearlyRecurrence = (startDate, endDate, event, complementaryObject) => {
    const localRecurringEvents = [];
    let currentDate = new Date(startDate);
    currentDate.setHours(0, 0, 0, 0);

    while (currentDate <= endDate) {
        const currentYear = currentDate.getFullYear();

        // Processa cada mês definido na recorrência
        event.recurrence.months.forEach((month) => {
            const monthOffSet = month - 1;
            if (event.recurrence.isOnThe) {
                localRecurringEvents.push(
                    ...generateOnTheRecurrence(
                        currentYear,
                        monthOffSet,
                        startDate,
                        endDate,
                        event.date,
                        event.recurrence.daysOfMonth,
                        complementaryObject
                    )
                );
            } else if (event.recurrence.isOn) {
                localRecurringEvents.push(
                    ...generateOnRecurrence(
                        currentYear,
                        monthOffSet,
                        startDate,
                        endDate,
                        event.date,
                        event.recurrence.weeksOfMonth,
                        event.recurrence.daysOfWeek,
                        complementaryObject
                    )
                );
            }
        });

        // Avança para o próximo ciclo anual, de acordo com a quantidade definida
        currentDate.setFullYear(currentDate.getFullYear() + event.recurrence.quantity);
    }

    return [...localRecurringEvents];
};

const createRecurringEvents = (eventList, lastYear, filter) => {
    let localFilter = filter ?? {
        min: new Date(1975, 0, 1, 0, 0, 0, 0),
        max: new Date(2074, 11, 31, 23, 59, 59, 999),
        months: [],
        years: []
    };

    const recurringEvents = [];
    const nonRecurringEvents = [];

    eventList.forEach((event) => {
        !event?.firstEventDate &&
            tryAddNonRecurringEvent(
                event.date,
                event.endTime,
                event.allDay,
                localFilter.min,
                localFilter.max,
                event,
                nonRecurringEvents
            );

        if (!event.isRecurrent || !event.recurrence) return;

        const { startDate, endDate } = formatDates(
            event?.recurrence?.startDate,
            event?.recurrence?.endDate,
            lastYear,
            localFilter
        );

        const { recurrence } = event;
        const complementaryObject = {
            ...event,
            firstEventDate: event.date
        };

        if (recurrence.frequencyId === frequency.day) {
            recurringEvents.push(
                ...generateDailyRecurrence(startDate, endDate, event, complementaryObject)
            );
        }

        if (recurrence.frequencyId === frequency.week) {
            recurringEvents.push(
                ...generateWeeklyRecurrence(startDate, endDate, event, complementaryObject)
            );
        }

        if (recurrence.frequencyId === frequency.month) {
            recurringEvents.push(
                ...generateMonthlyRecurrence(startDate, endDate, event, complementaryObject)
            );
        }

        if (recurrence.frequencyId === frequency.year) {
            recurringEvents.push(
                ...generateYearlyRecurrence(startDate, endDate, event, complementaryObject)
            );
        }
    });

    const allEvents = [...nonRecurringEvents, ...recurringEvents].filter((event) => {
        const eventDate = new Date(event.date);
        const minDate = new Date(localFilter.min);
        const maxDate = new Date(localFilter.max);

        if (event.allDay) {
            eventDate?.setHours?.(23, 59, 59, 999);
        } else {
            const [endHour, endMinute] = event?.endTime?.split(":") ?? [0, 0];

            eventDate?.setHours?.(endHour, endMinute, 0, 0);
        }

        let rangeValidation = true;
        let monthValidation = true;
        let yearValidation = true;

        if (filter?.months?.length) {
            monthValidation = filter.months.includes(eventDate.getMonth());
        }

        if (filter?.years?.length) {
            yearValidation = filter.years.includes(eventDate.getFullYear());
        }

        rangeValidation = eventDate >= minDate && eventDate <= maxDate;

        return rangeValidation && monthValidation && yearValidation;
    });

    return allEvents.sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);

        if (a.allDay) {
            dateA?.setHours?.(0, 0, 0, 0);
        } else {
            const [startHour, startMinute] = a.startTime?.split(":") ?? [0, 0];
            dateA?.setHours?.(startHour, startMinute, 0, 0);
        }

        if (b.allDay) {
            dateB?.setHours?.(0, 0, 0, 0);
        } else {
            const [startHour, startMinute] = b.startTime?.split(":") ?? [0, 0];
            dateB?.setHours?.(startHour, startMinute, 0, 0);
        }

        return dateA - dateB;
    });
};

export { createRecurringEvents };
